import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponentLike } from 'prop-types';
import ExpressionEngine from '../../editor/expressions/expression-engine';
import { useApp } from '../../hooks/use-app';
import { ChangeTypeByKey, getFeatureVariables } from './helpers';
import { Record, RecordTerms } from '../../editor/record.types';
import { ParentRecordState } from '..';
import dataEventModel from './data-event-model';
import dataEventTrigger from './data-event-trigger';
import { EventContext } from './types';
import { VALIDATORS } from '../../utils/validate-form-fields';
import { KEYS } from '../../hooks/helpers/instructions/basic-task';

interface Props {
  form?: any;
  record: Record<object>;
  onChange: (record: Record<object>) => void;
  fields: Array<Element>;
  errorsFields?: Array<string>,
  openRecordInEditor: (record?: Record<any>, formData?: any) => Promise<void>;
  linkSelectedRecords: (key: string, taskIDs: any[], parentRecordState: ParentRecordState) => void;
  terminology?: RecordTerms,
  handleClose?: () => void,
}

// NOTE: possibly get this from a 'record_series_key' on the form?
export const getDateValue = (fields, record: Record<object>) => (
  record.form_values[KEYS.dueDate] === VALIDATORS.DATE ? '' : record.form_values[KEYS.dueDate]
);

const withDataEvents = (WrappedComponent: ReactComponentLike) => ((props: Props) => {
  const {
    form, record, onChange, fields, ...rest
  } = props;
  const feature = form?.systemAppSchema?.form;
  const app = useApp();
  const queueEvent = dataEventModel();
  const triggerEvent = dataEventTrigger();
  const elementsByKey = useMemo(() => getElementsByKey(form?.restForm?.elements), [form]);
  const [dataEventsContext, setDataEventsContext] = useState<EventContext | null>(null);

  useEffect(() => {
    ExpressionEngine.initializeRootEngine(app?.globals?.config?.sandbox_url);
    const { ...generatedContext } = queueEvent.buildContext({
      form: feature,
      engine: ExpressionEngine.acquire(
        WrappedComponent,
        app.globals.config.sandbox_url,
      ),
      getFeatureVariables: getFeatureVariables({ app, record, feature }),
      onChange,
      record,
      elementsByKey,
    });

    setDataEventsContext(generatedContext);
  }, [form]);

  const handleChange = (
    field: any,
    value: string,
    callback: () => void = () => {},
  ) => {
    const evt = {
      name: ChangeTypeByKey[field.key] || ChangeTypeByKey.key,
      field: field.dataName || field.data_name || field,
      key: field.key,
      value,
    };

    if (dataEventsContext) {
      triggerEvent.current({ evt, callback }, dataEventsContext);
    }
  };

  return (
    <WrappedComponent
      formData={form}
      record={record}
      fields={fields}
      errorsFields={rest.errorsFields}
      onChange={handleChange}
      openRecordInEditor={rest.openRecordInEditor}
      linkSelectedRecords={rest.linkSelectedRecords}
      terminology={rest.terminology}
      handleClose={() => (rest.handleClose ? rest.handleClose() : null)}
    />
  );
});

function getElementsByKey(elements: { key: string, elements: any }[] = []) {
  /* eslint-disable no-param-reassign */
  return elements.reduce((elementsByKey, element) => {
    elementsByKey[element.key] = element;
    if (element.elements) {
      elementsByKey = { ...elementsByKey, ...getElementsByKey(element.elements) };
    }
    return elementsByKey;
  }, {});
  /* eslint-enable no-param-reassign */
}

export default withDataEvents;
